import React, { useEffect, useState } from "react";
import axios from "../axiosSetup";
import { FiSearch, FiPlus, FiX, FiUsers, FiGrid, FiEye } from "react-icons/fi";
import CustomerDetailDrawer from "../components/CustomerDetailDrawer";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import CustomSelect from "../components/CustomSelect";
import ConfirmModal from "../components/ConfirmModal";
import "./UserManagement.css";
import { getUser } from "../auth";
import { useAccess } from "../hooks/useAccess";
import editIcon from "../assets/edit.svg";
import deleteIcon from "../assets/delete.svg";
import { useOverflowTooltip } from "../hooks/useOverflowTooltip";
import CustomTooltip from "../components/CustomTooltip"; 
import ViewUserRoleModal from "../components/ViewUserRoleModal";
import Breadcrumbs from "../components/Breadcrumbs";
import userControllerIcon from "../assets/userControlerIcon.svg";
import UserControllerModal from "../components/UserControllerModal";  
import noFilterIcon from '../assets/no-filter.svg';
import EntityMultiSelect from "../components/EntityMultiSelect";
import qs from "qs";

// Axios setup
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;
axios.defaults.withCredentials = true;
axios.defaults.headers.common["Content-Type"] = "application/json";

function debounce(func, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
}

const AnalyticsCard = ({ title, value, onClick}) => {
  title = title
    ? title + " Customers"
    : "Total Customers";
  return <div className="analytics-card" onClick={onClick}>
    <div className="analytics-card-title">{title}</div>
    <div className="analytics-card-value">{value}</div>
  </div>;
}

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("users");
  const [roleFilter, setRoleFilter] = useState("All Role");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isUserModalLoading, setIsUserModalLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
const [isFetching, setIsFetching] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [page, setPage] = useState(1);
  const [pageByTab, setPageByTab] = useState({ users: 1, customers: 1 });
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPagesByTab, setTotalPagesByTab] = useState({ users: 1, customers: 1 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalRecordsByTab, setTotalRecordsByTab] = useState({ users: 0, customers: 0 });
  const [deleteUserId, setDeleteUserId] = useState(null);
const [showDeleteModal, setShowDeleteModal] = useState(false);
const [showRoleViewModal, setShowRoleViewModal] = useState(false);
const [selectedRoleView, setSelectedRoleView] = useState("");
const [showUserControlModal, setShowUserControlModal] = useState(false);
const [selectedUserForControl, setSelectedUserForControl] = useState(null);
const [roleOptions, setRoleOptions] = useState(["All Role"]);
const [isRoleLoading, setIsRoleLoading] = useState(false);
const USER_MGMT_STORAGE_KEY = "user-management-state";
const rolesFetchedRef = React.useRef(false);
const abortControllerRef = React.useRef(null);
const [entityOptions, setEntityOptions] = useState([]);
const [entityFilter, setEntityFilter] = useState([]);
const [customerAnalytics, setCustomerAnalytics] = useState([]);
const [selectedCustomer, setSelectedCustomer] = useState(null);
const [isCustomerDetailLoading, setIsCustomerDetailLoading] = useState(false);
const [showCustomerDetail, setShowCustomerDetail] = useState(false);
const [userAnalytics, setUserAnalytics] = useState([]);
const requiredAnalytics=React.useRef(true);

const getUserMgmtPermissions = () => {
  try {
    const stored = sessionStorage.getItem("APP_USER_INFO");
    if (!stored) return {};

    const user = JSON.parse(stored);

    const userMgmtService = user.services?.find(
      (s) => s.service === "User Management"
    );

    const ops = userMgmtService?.operations || [];

    const canCreate = ops.includes("CREATE");
    const canEdit = ops.includes("UPDATE");
    const canDelete = ops.includes("DELETE");

    return {
      canAdd: canCreate,        // ✅ as requested
      canEdit: canEdit,
      canDelete: canDelete,
      canToggle: canEdit,       // ✅ toggle = UPDATE
    };
  } catch {
    return {
      canAdd: false,
      canEdit: false,
      canDelete: false,
      canToggle: false,
    };
  }
};


const getPaginationPages = (current, total) => {
  const pages = [];

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  if (current <= 3) {
    return [1, 2, 3, 4, "...", total];
  }

  if (current >= total - 2) {
    return [1, "...", total - 3, total - 2, total - 1, total];
  }

  return [
    1,
    "...",
    current - 1,
    current,
    current + 1,
    "...",
    total,
  ];
};


// Fetch user roles for Role filter dropdown
useEffect(() => {
  if (rolesFetchedRef.current) return;

  rolesFetchedRef.current = true;
  fetchUserRoles();
}, []);



//  Unified confirmation modal
const [confirmConfig, setConfirmConfig] = useState({
  open: false,
  title: "",
  message: "",
  confirmText: "",
  confirmType: "primary", // primary or danger
  onConfirm: () => {},
});

  const NAME_MAX_LENGTH = 255;

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    role: "",
  });

  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "",
    active: true,
  });
  

  const { canAdd, canEdit, canDelete, canToggle } = getUserMgmtPermissions();
  const currentUser = getUser();
  const isCurrentUserAdmin =
    String(currentUser?.role || "").trim().toLowerCase() === "admin";
  const isViewOnly = !canAdd && !canEdit && !canDelete;
  const isCustomersTab = activeTab === "customers";
  const shouldShowActions = !isViewOnly && !isCustomersTab;
  const isTableLoading = isInitialLoading || isFetching;
  const tableColumnCount = isCustomersTab ? 5 : shouldShowActions ? 5 : 4;
  const paginationStart = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
  const paginationEnd = Math.min(page * pageSize, totalRecords);
  const isInitialLoad = React.useRef(true);
  const [hasRestored, setHasRestored] = useState(false);
useEffect(() => {
  if (isInitialLoad.current) {
    isInitialLoad.current = false;
    return;
  }
  setIsFetching(true);
  setUsers([]);       
  setTotalPages(1);
  setTotalRecords(0);
  setPage(1);
}, [roleFilter, statusFilter, debouncedSearchQuery]);

useEffect(() => {
  if(!isCustomersTab || entityOptions.length>0) return;
  const abortController = new AbortController();
  const fetchEntities = async () => {
    try {
      const res = await axios.get("/dropdown/entity", {
        signal: abortController.signal,
      });
      setEntityOptions(Array.isArray(res.data) ? res.data.map(e=>({ value: e?.entity, label: e?.entity })) : []);
    } catch (err) {}  
  };
  fetchEntities();
  return () => {
    abortController.abort();
  };
},[activeTab]);

useEffect(() => {
  const nextSearch = searchQuery.trim();

  if (nextSearch.length > 0 && nextSearch.length < 3) return;

  const timer = setTimeout(() => {
    setDebouncedSearchQuery(nextSearch);
  }, 400);

  return () => clearTimeout(timer);
}, [searchQuery]);

 const fetchUsers = async (currentPage = page, currentLimit = pageSize) => {
  const requestTab = activeTab;
  const requestIsCustomers = requestTab === "customers";
  // cancel previous request
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }

  const controller = new AbortController();
  abortControllerRef.current = controller;

  const isFirstLoad = !hasRestored || isInitialLoading;

  isFirstLoad ? setIsInitialLoading(true) : setIsFetching(true);
  setErrorMsg("");

  try {
    const params = {
      page: currentPage,
      limit: currentLimit,
      isCustomers: requestIsCustomers,
      requiredAnalytics:requiredAnalytics.current,
    };

    if (debouncedSearchQuery) params.search = debouncedSearchQuery;
    if (!requestIsCustomers && roleFilter !== "All Role") {
      params.role = roleFilter;
    }
    if (!requestIsCustomers && statusFilter !== "All Status") {
      params.active = statusFilter === "Active";
    }
    if (requestIsCustomers && entityFilter.length > 0) {
      params.entity = entityFilter;
    }
    const res = await axios.get("/user/", {
      params,
      signal: controller.signal,
      paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }),
    });

    const nextRows = requestIsCustomers
      ? res.data?.customers || []
      : res.data?.users || [];
    const nextTotalPages = res.data?.totalPages || 1;
    const nextTotalRecords = Number(res.data?.total ?? nextRows.length);

    if (requiredAnalytics.current && requestIsCustomers) {
      setCustomerAnalytics(res.data?.customerAnalytics || []);
    } else if(requiredAnalytics.current){
      setUserAnalytics(res.data?.userAnalytics || []);
    }
    setUsers(nextRows);
    setTotalPages(nextTotalPages);
    setTotalRecords(nextTotalRecords);
    setTotalPagesByTab((prev) => ({
      ...prev,
      [requestTab]: nextTotalPages,
    }));
    setTotalRecordsByTab((prev) => ({
      ...prev,
      [requestTab]: nextTotalRecords,
    }));
    requiredAnalytics.current=false;
  } catch (err) {
    if (err.name === "CanceledError") return; // ✅ expected
    console.error("Error fetching users:", err);
    setErrorMsg("Failed to load users.");
  } finally {
  if (controller.signal.aborted) {
    return;
  }

  isFirstLoad
    ? setIsInitialLoading(false)
    : setIsFetching(false);
}
};



useEffect(() => {
  const saved = sessionStorage.getItem(USER_MGMT_STORAGE_KEY);

  if (saved) {
    try {
      const parsed = JSON.parse(saved);

      const savedActiveTab = parsed.activeTab ?? "users";
      const savedPageByTab = parsed.pageByTab ?? {
        users: parsed.page ?? 1,
        customers: 1,
      };

      setActiveTab(savedActiveTab);
      setPageByTab(savedPageByTab);
      setPage(savedPageByTab[savedActiveTab] ?? 1);
      setTotalPagesByTab(parsed.totalPagesByTab ?? { users: 1, customers: 1 });
      setTotalPages(parsed.totalPagesByTab?.[savedActiveTab] ?? 1);
      setTotalRecordsByTab(parsed.totalRecordsByTab ?? { users: 0, customers: 0 });
      setTotalRecords(parsed.totalRecordsByTab?.[savedActiveTab] ?? 0);
      setSearchQuery(parsed.searchQuery ?? "");
      setDebouncedSearchQuery((parsed.searchQuery ?? "").trim());
      setRoleFilter(parsed.roleFilter ?? "All Role");
      setStatusFilter(parsed.statusFilter ?? "All Status");
    } catch {
      sessionStorage.removeItem(USER_MGMT_STORAGE_KEY);
    }
  }

  // 🔥 THIS IS CRITICAL
  setHasRestored(true);
}, []);

const handleReset = () => {
  setSearchQuery("");
  if (!isCustomersTab) {
    setRoleFilter("All Role");
    setStatusFilter("All Status");
  }
};

const handleTabChange = (nextTab) => {
  if (nextTab === activeTab) return;

  setUsers([]);
  setIsFetching(true);
  setPageByTab((prev) => ({
    ...prev,
    [activeTab]: page,
  }));
  setActiveTab(nextTab);
  requiredAnalytics.current=true;
  setSearchQuery("");
  setPage(pageByTab[nextTab] ?? 1);
  setTotalPages(totalPagesByTab[nextTab] ?? 1);
  setTotalRecords(totalRecordsByTab[nextTab] ?? 0);
};

useEffect(() => {
  setPageByTab((prev) =>
    prev[activeTab] === page ? prev : { ...prev, [activeTab]: page }
  );
}, [activeTab, page]);

useEffect(() => {
  sessionStorage.setItem(
    USER_MGMT_STORAGE_KEY,
    JSON.stringify({
      page,
      pageByTab,
      totalPagesByTab,
      totalRecordsByTab,
      activeTab,
      searchQuery,
      roleFilter,
      statusFilter,
    })
  );
}, [page, pageByTab, totalPagesByTab, totalRecordsByTab, activeTab, searchQuery, roleFilter, statusFilter]);
useEffect(() => {
  return () => {
    sessionStorage.removeItem(USER_MGMT_STORAGE_KEY);
  };
}, []);
// useEffect(() => {
//   // reset to page 1 when filters change
//   setPage(1);
// }, [roleFilter, statusFilter, searchQuery]);

const fetchUserRoles = async () => {
  setIsRoleLoading(true);

  try {
    const res = await axios.get("/dropdown/user-roles");
    const apiRoles = Array.isArray(res.data) ? res.data : [];

    setRoleOptions([
      { label: "All Role", value: "All Role" },
      ...apiRoles.map(role => ({
        label: role,
        value: role,
      })),
    ]);
  } catch (err) {
    console.error("Failed to fetch user roles", err);
    setRoleOptions([
      { label: "All Role", value: "All Role" },
    ]);
  } finally {
    setIsRoleLoading(false);
  }
};



useEffect(() => {
  if (!hasRestored) return; // 🔒 BLOCK premature fetch
  fetchUsers(page, pageSize);
}, [hasRestored, page, activeTab, debouncedSearchQuery, roleFilter, statusFilter,entityFilter]);

  useEffect(() => {
  if (page > totalPages) {
    setPage(totalPages || 1);  // avoid page=0
  }
}, [totalPages]);

const validateForm = () => {
  const newErrors = { name: "", email: "", role: "" };
  let isValid = true;

  if (!newUser.name.trim()) {
    newErrors.name = "Name is required.";
    isValid = false;
  } else if (newUser.name.length > NAME_MAX_LENGTH) {
    //  NO trimming here – use raw length
    newErrors.name = `Name cannot exceed ${NAME_MAX_LENGTH} characters.`;
    isValid = false;
  }

  if (!newUser.email.trim()) {
  newErrors.email = "Email is required.";
  isValid = false;
} 
else if (newUser.email.length > NAME_MAX_LENGTH) {
  newErrors.email = `Email cannot exceed ${NAME_MAX_LENGTH} characters.`;
  isValid = false;
}
else if (!newUser.email.toLowerCase().endsWith("@vocovo.com")) {
  newErrors.email = "Email must end with @vocovo.com";
  isValid = false;
}
else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)) {
  newErrors.email = "Enter a valid email address.";
  isValid = false;
}


  if (!newUser.role.trim()) {
    newErrors.role = "Role is required.";
    isValid = false;
  }

  setErrors(newErrors);
  return isValid;
};
const handleUserControl = (user) => {
  setSelectedUserForControl(user);
  setShowUserControlModal(true);
};


  const handleSaveUser = async () => {
    if (!validateForm()) return;

    const payload = {
      ...newUser,
      deleted: false,
      updatedAt: new Date().toISOString(),
    };

    try {
      setIsUserModalLoading(true);
      if (editingUser) {
        await axios.put(`/user/${editingUser.id}`, {
          ...payload,
          id: editingUser.id,
        });
      } else {
        await axios.post("/user/", {
          ...payload,
          createdAt: new Date().toISOString(),
        });
      }

      setShowModal(false);
      setEditingUser(null);
      setNewUser({ name: "", email: "", role: "", active: true });
      fetchUsers();
    } catch (err) {
  const backend = err?.response?.data || {};
  const status = err?.response?.status;

  /* ---------------- DUPLICATE / CONFLICT ---------------- */
  if (status === 409) {
    const msg = backend.message?.toLowerCase() || "";
    const code = backend.code || "";
    const details = backend.details || [];

    const detailText =
      Array.isArray(details) && details.length
        ? details.join(" ").toLowerCase()
        : "";

    if (
      code === "UNIQUE_CONSTRAINT_ERROR" ||
      msg.includes("duplicate") ||
      detailText.includes("unique")
    ) {
      setErrors(prev => ({
        ...prev,
        email: "This email already exists.",
      }));
      return;
    }

    if (msg.includes("deleted")) {
      setErrors(prev => ({
        ...prev,
        email:
          "A deleted user already exists with this email. Please restore that user.",
      }));
      return;
    }
  }

  /* ---------------- FIELD VALIDATION ERRORS ---------------- */
  const details = backend.details;

  if (Array.isArray(details) && details.length > 0) {
    const emailError = details.find(d =>
      d.toLowerCase().startsWith("email:")
    );

    if (emailError) {
      setErrors(prev => ({
        ...prev,
        email: emailError.split(":").slice(1).join(":").trim(),
      }));
      return;
    }
  }

  /* ---------------- GENERIC FALLBACK ---------------- */
  setErrors(prev => ({
    ...prev,
    email:
      backend.message ||
      backend.error ||
      "Could not save user. Please try again.",
  }));
    } finally {
      setIsUserModalLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
  try {
    await axios.delete(`/user/${id}`);

    // Remove record locally
    const updatedList = users.filter((u) => u.id !== id);
    const remaining = updatedList.length;

    // CASE 1 — There are still records on this page
    if (remaining > 0) {
      setUsers(updatedList);
    } 
    
    // CASE 2 — Page becomes empty → go to previous page if possible
    else {
      if (page > 1) {
        setPage(page - 1);
      }
    }

    //  After adjusting page, always refetch with correct current page
    fetchUsers(page > 1 && remaining === 0 ? page - 1 : page, pageSize);

  } catch (err) {
    console.error("Error deleting user:", err);
    alert("Failed to delete user.");
  }
};
const handleToggleStatus = async (user) => {
  try {
    const increment=!user.active;
    const updated = { ...user, active: !user.active };

    // Update backend
    await axios.patch(`/user/${user.id}/active`, { active: updated.active });

    // Update ONLY this user in UI
    setUsers((prevUsers) =>
      prevUsers.map((u) =>
        u.id === user.id ? { ...u, active: updated.active } : u
      )
    );
    setUserAnalytics(prev =>
      prev.map(a =>
        a.role === undefined || a.role===null || user.role === a.role
          ? { 
            ...a, 
            active: increment
             ? a.active+1
             : Math.max(0, a.active - 1),
          }
          : a
      )
    );

  } catch (err) {
    console.error("Error updating status:", err);
    alert("Failed to update status.");
  }
};


  // const handleToggleStatus = async (user) => {
  //   try {
  //     const updatedUser = {
  //       ...user,
  //       active: !user.active,
  //       updatedAt: new Date().toISOString(),
  //     };
  //     await axios.put(`/user/${user.id}`, updatedUser);
  //     fetchUsers();
  //   } catch (err) {
  //     console.error("Error updating status:", err);
  //     alert("Failed to update status.");
  //   }
  // };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setNewUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      active: user.active,
    });
    setShowModal(true);
  };
  // 🔹 Open confirm modal
const openConfirm = (config) => {
  setConfirmConfig({
    open: true,
    title: config.title || "Confirm",
    message: config.message || "Are you sure?",
    confirmText: config.confirmText || "Confirm",
    confirmType: config.confirmType || "primary",
    onConfirm: config.onConfirm || (() => {}),
  });
};

// 🔹 Close modal
const closeConfirm = () => {
  setConfirmConfig((prev) => ({ ...prev, open: false }));
};

// 🔹 Handle confirm click
const handleConfirmClick = async () => {
  await confirmConfig.onConfirm();
  closeConfirm();
};

const isUserFormValid = () => {
  return (
    newUser.name.trim() &&
    newUser.email.trim() &&
    newUser.role.trim() &&
    !errors.name &&
    !errors.email &&
    !errors.role
  );
};

const handleViewCustomer = async (customerId) => {
  setShowCustomerDetail(true);
  setIsCustomerDetailLoading(true);
  try {
    const res = await axios.get(`/user/${customerId}`, {
      params: { isCustomer: "true" }
    });
    setSelectedCustomer(res.data);
  } catch (err) {
    console.error("Failed to fetch customer detail", err);
  } finally {
    setIsCustomerDetailLoading(false);
  }
};


  return (
    <div className="user-container">
      {/* HEADER */}
      <div className="user-header">
        <div>
          <Breadcrumbs />
          {/* <h1>User Management</h1>
          <p>Manage users, roles, and permissions</p> */}
        </div>
        <div/>

        
      </div>

      {/* CARD */}
      <div className="user-card">
        <div className="user-tabs" role="tablist" aria-label="User management views">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "users"}
            className={`user-tab ${activeTab === "users" ? "active" : ""}`}
            onClick={() => handleTabChange("users")}
          >
            <FiUsers className="user-tab-icon" aria-hidden="true" />
            Users
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "customers"}
            className={`user-tab ${activeTab === "customers" ? "active" : ""}`}
            onClick={() => handleTabChange("customers")}
          >
            <FiGrid className="user-tab-icon" aria-hidden="true" />
            Customers
          </button>
        </div>

        <div className="user-tab-panel">
          {!isCustomersTab && (
            <div className="user-analytics-horizontal">
              <div className="analytics-cards-row">
                {userAnalytics.map((item, index) => {
                  const label = item.role || "All Users";
                  const total = item.total;
                  const active = item.active;
                  const inactive = total - active;
                  const percentage =
                    total === 0 ? 0 : Math.round((active / total) * 100);

                  return (
                    <div
                      key={index}
                      className="analytics-card-h"
                      onClick={() => {
                        if (item.role && roleFilter !== item.role) {
                          setIsFetching(true);
                          setRoleFilter(item.role);
                          setPage(1);
                        } else if (!item.role && roleFilter!=="All Role"){
                          setIsFetching(true);
                          setRoleFilter("All Role");
                          setPage(1);
                        }
                      }}
                    >
                      <div className="card-h-header">
                        <div className="card-h-title">{label}</div>
                        <div className="card-h-count">{total}</div>
                      </div>

                      <div className="card-h-progress">
                        <div className="card-h-bar">
                          <div
                            className="card-h-bar-active"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>

                        <div className="card-h-labels">
                          <span className="card-h-label-active">
                            {active} Active
                          </span>
                          <span className="card-h-label-inactive">
                            {inactive} Inactive
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {isCustomersTab && (
            <div className="customer-analytics">
              {customerAnalytics.map(ca => (
                <AnalyticsCard 
                  key={ca.entity} 
                  title={ca.entity} 
                  value={ca.customers} 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if(ca.entity && entityFilter[0] !== ca.entity){
                      setIsFetching(true);
                      setEntityFilter([ca.entity]);
                    }else if(!ca.entity && entityFilter.length > 0){
                      setIsFetching(true);
                      setEntityFilter([]);
                    }
                  }} 
                />
              ))}
            </div>
          )}
        {/* FILTERS */}
        <div className="user-filters" style={{ justifyContent: isCustomersTab ? "flex-start" : "space-between" }}>
          {(isCustomersTab &&
            <div width="fit-content">
              <EntityMultiSelect
                options={entityOptions.map(e=>e.value).filter(Boolean)}
                selected={entityFilter}
                onChange={(updatedSelected) => {
                  setEntityFilter(updatedSelected);
                }}
              />
            </div>
          )}
          <div className="search-box1">
            <FiSearch className="search-icon" aria-hidden="true" />
            <input
              type="text"
              style={{ color: "#9A49CD" }}
              placeholder={
                isCustomersTab
                  ? "Search by customer code, name or email..."
                  : "Search by name, email, or role..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-box1-input"
            />
          </div>

          {!isCustomersTab 
            && (
            <div className="filter-dropdowns">
            <CustomSelect
              value={roleFilter}
              onChange={(v) => setRoleFilter(v)}
              width="15em"
              options={roleOptions}
              disabled={isRoleLoading}
            />
            <CustomSelect
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              width="10em"
              options={["All Status", "Active", "Inactive"]}
            />
            <div className="pm-header-right">
              <CustomTooltip message="Clear filters" placement="top" color="#4A1476">
                <button
                  type="button"
                  className="no-filter-reset"
                  onClick={handleReset}
                  aria-label="Clear filters"
                >
                  <span
                    className="no-filter-reset-icon"
                    style={{ WebkitMaskImage: `url(${noFilterIcon})`, maskImage: `url(${noFilterIcon})` }}
                  />
                </button>
              </CustomTooltip>
            </div>
            
            {canAdd && (
                  <button
                    className="add-user-btn"
                    onClick={() => {
                      setShowModal(true);
                      setEditingUser(null);
                      setNewUser({ name: "", email: "", role: "", active: true });
                      setErrors({ name: "", email: "", role: "" });
                    }}
                  >
                    <FiPlus /> Add User
                  </button>
                )}
            </div>
          )}
        </div>

        {isCustomersTab && (
          <div className="customer-sync-note">
            These details are synced from Unleashed and are not created directly on the Digital Platform.
          </div>
        )}

        {/* TABLE */}
          <div className="table-wrapper">
<table className={`user-table ${isCustomersTab ? "customer-table" : ""} ${!shouldShowActions ? "hide-actions" : ""}`.trim()}>
              <colgroup>
                {isCustomersTab ? (
                  <>
                    <col style={{ width: "170px" }} />
                    <col style={{ width: "35%" }} />
                    <col />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "80px" }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: shouldShowActions ? "28%" : "34%" }} />
                    <col />
                    <col style={{ width: "170px" }} />
                    <col style={{ width: "130px" }} />
                    {shouldShowActions && <col style={{ width: "170px" }} />}
                  </>
                )}
              </colgroup>
              <thead>
                <tr>
                  {isCustomersTab && <th>Customer Code</th>}
                  <th>{isCustomersTab ? "Customer Full Name" : "User Full Name"}</th>
                  <th>Email</th>
                  {isCustomersTab 
                    ?(<> 
                        <th className="entity-col">Entity</th>
                         <th className="view-col" style={{ textAlign: "center" }}>View</th> 
                      </>) 
                    :(<>
                        <th className="role-col">Role</th>
                        <th className="status-col">Status</th>
                      </>)
                  }
                  {shouldShowActions && (
  <th className="actions-col">Actions</th>
)}

                </tr>
              </thead>

              <tbody>
  {isTableLoading ? (
    <tr>
      <td colSpan={tableColumnCount} className="table-loading-cell">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 34, color: "#f50087" }} spin />} />
        <span>Loading</span>
      </td>
    </tr>
  ) : users.length > 0 ? (
    users.map((user) => (
      <tr key={user.id}>
        {isCustomersTab && (
          <td className="number-col">
            <CustomTooltip
              message={user.customerCode}
              placement="top"
              color="#4A1476"
            >
              <span className="truncate-cell">{user.customerCode || "-"}</span>
            </CustomTooltip>
          </td>
        )}


        {/* NAME */}
        <td className="text-col">
          <CustomTooltip message={user.name} placement="top" color="#4A1476">
            <span className="truncate-cell">{user.name?.charAt(0).toUpperCase() + user.name?.slice(1)}</span>
          </CustomTooltip>
        </td>

        {/* EMAIL */}
        <td className="text-col">
          <CustomTooltip
            message={user.email?.trim()}
            placement="top"
            color="#4A1476"
          >
            <span className="truncate-cell">
              {user.email?.trim()}
            </span>
          </CustomTooltip>
        </td>


        {isCustomersTab
         ?(
          <>
            <td style={{textAlign:"center"}} className="text-col">{user.entity}</td>
             <td style={{ textAlign: "center" }} className="view-col">
      <CustomTooltip message="View customer details" placement="top" color="#4A1476">
        <button
          className="icon-btn"
          onClick={(e) => { e.stopPropagation(); handleViewCustomer(user.id); }}
        >
          <FiEye style={{ width: 20, height: 20, color: "#7509B8" }} />
        </button>
      </CustomTooltip>
    </td>
          </>
         )
         :(
          <>
            {/* ROLE */}
            <td className="text-col">{user.role}</td>

            {/* STATUS */}
            <td className="text-col status-cell">
              <div className="status-toggle-wrapper">
                {user.role === "Vocovo Customer" ? (
                  // EMPTY PLACEHOLDER (same space, no toggle)
                  <div
                      style={{
                        width: 35,        // same as toggle width
                        height: 22,       // same as toggle height
                        flexShrink: 0,    // prevents collapse
                      }}
                    />
                ) : (
                  // REAL TOGGLE FOR ALL OTHER ROLES
                  <div
                    className={`status-toggle ${user.active ? "active" : ""} ${canToggle ? "" : "disabled"}`.trim()}
                    onClick={canToggle ? (e) => {
                      e.stopPropagation();
                      const action = user.active ? "Deactivate" : "Activate";
                      openConfirm({
                        title: `${action} User`,
                        message: `Are you sure you want to ${action.toLowerCase()} this user?`,
                        confirmText: action,
                        confirmType: user.active ? "danger" : "primary",
                        onConfirm: async () =>  handleToggleStatus(user),
                      });
                    }: undefined}
                  >
                    <div className="toggle-circle"></div>
                  </div>
                )}

                <span
                  className={`status-text ${user.active ? "active" : "inactive"}`}
                >
                  {user.active ? "Active" : "Inactive"}
                </span>
              </div>
            </td>
          </>
        )}
        {/* ACTIONS */}
        {shouldShowActions && (
          <td className="actions actions-col">
            <span>
            {/* EDIT */}
            {canEdit && user.role !== "Vocovo Customer" && (
              <CustomTooltip message="Edit User details" placement="top" color="#4A1476">
                <button
                  className="icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditUser(user);
                  }}
                >
                  <img src={editIcon} alt="edit" style={{ width: 32, height: 32 }} />
                </button>
              </CustomTooltip>
            )}</span>
            <span>
            {/* DELETE */}
             {canDelete && user.role !== "Vocovo Customer" && (
              <CustomTooltip message="Delete User details" placement="top" color="#4A1476">
                <button
                  className="icon-btn delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    openConfirm({
                      title: "Confirm Delete",
                      message: "Are you sure you want to delete this user?",
                      confirmText: "Delete",
                      confirmType: "danger",
                      onConfirm: async () => handleDeleteUser(user.id),
                    });
                  }}
                >
                  <img src={deleteIcon} alt="delete" style={{ width: 32, height: 32 }} />
                </button>
              </CustomTooltip>
            )}</span>
            <span>
            {/* USER CONTROL — hide for Admin users */}
  {canAdd && user.role !== "Admin" && user.role !== "Vocovo Customer" && (
  <CustomTooltip message="User Customer Controller" placement="top" color="#4A1476">
    <button
      className="icon-btn user-controller-btn"
      onClick={(e) => {
        e.stopPropagation();
        handleUserControl(user);
      }}
    >
      <img
        src={userControllerIcon}
        alt="user controller"
        style={{ width: 32, height: 32 }} 
      />
    </button>
  </CustomTooltip>
)}
</span>
          </td>
        )}
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan={tableColumnCount} style={{ textAlign: "center", padding: 20 }}>
        {isCustomersTab ? "No customers found." : "No users found."}
      </td>
    </tr>
  )}
</tbody>

            </table>

            <div className="pagination-wrapper">
              <div className="pg-info">{paginationStart} - {paginationEnd} of {totalRecords}</div>
              <div className="pg-buttons">
  <button
    className="pg-arrow"
    disabled={page === 1}
    onClick={() => setPage(page - 1)}
  >
    ‹
  </button>

 {getPaginationPages(page, totalPages).map((p, i) =>
  p === "..." ? (
    <button
      key={`dots-${i}`}
      className="pg-dots"
      onClick={() => {
        if (i === 1) setPage(page - 2); // left dots
        else setPage(page + 2);        // right dots
      }}
    >
      …
    </button>
  ) : (
    <button
      key={p}
      className={`pg-number ${page === p ? "active" : ""}`}
      onClick={() => setPage(p)}
    >
      {p}
    </button>
  )
)}


  <button
    className="pg-arrow"
    disabled={page === totalPages}
    onClick={() => setPage(page + 1)}
  >
    ›
  </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {showModal && canAdd && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingUser ? "Edit User" : "Add New User"}</h2>
              <FiX className="close-icon" onClick={() => setShowModal(false)} />
            </div>

            <p className="modal-subtitle">
              {editingUser
                ? "Update user information and permissions"
                : "Create a new user account"}
            </p>

            {isUserModalLoading ? (
              <div style={{ minHeight: "220px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 34, color: "#f50087" }} spin />} />
                <span style={{ color: "#f50087", fontWeight: 600 }}>Loading</span>
              </div>
            ) : (
            <div className="modal-body">
              <label>Name <span className="required-asterisk">*</span></label>
              <input
                type="text"
                placeholder="Enter Name"
                autoComplete="off"
                value={newUser.name}
 onChange={(e) => {
    const raw = e.target.value;

    // Store full value, even if > 255
    setNewUser({ ...newUser, name: raw });

    // Show / clear error live
    if (raw.length > NAME_MAX_LENGTH) {
      setErrors((prev) => ({
        ...prev,
        name: `Maximum ${NAME_MAX_LENGTH} characters allowed.`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  }}

              />
              {errors.name && (
                <p className="uc-error-text">{errors.name}</p>
              )}

              <label>Email <span className="required-asterisk">*</span></label>
              <input
  type="email"
  placeholder="Enter Email"
  value={newUser.email}
  onChange={(e) => {
  const raw = e.target.value;
  setNewUser({ ...newUser, email: raw }); // ❗ Always allow typing

  const local = raw.split("@")[0];

  // 1. Email cannot start with special char
  if (/^[^A-Za-z0-9]/.test(raw)) {
    setErrors(prev => ({ ...prev, email: "Email cannot start with a special character." }));
    return;
  }

  // 2. Max length
  if (raw.length > NAME_MAX_LENGTH) {
    setErrors(prev => ({ ...prev, email: `Maximum ${NAME_MAX_LENGTH} characters allowed.` }));
    return;
  }

  // 3. No spaces
  if (/\s/.test(raw)) {
    setErrors(prev => ({ ...prev, email: "Email cannot contain spaces." }));
    return;
  }

  // 4. Must contain "@"
  if (!raw.includes("@")) {
    setErrors(prev => ({ ...prev, email: "Enter a valid email address." }));
    return;
  }

  // 5. Must end with @vocovo.com
  if (!raw.toLowerCase().endsWith("@vocovo.com")) {
    setErrors(prev => ({ ...prev, email: "Email must end with @vocovo.com" }));
    return;
  }

  // 6. Only ASCII allowed
  if (!/^[\x00-\x7F]+$/.test(local)) {
    setErrors(prev => ({ ...prev, email: "Email cannot contain emojis or non-ASCII characters." }));
    return;
  }

  // 7. Allowed characters only
  if (!/^[A-Za-z0-9._-]+$/.test(local)) {
    setErrors(prev => ({
      ...prev,
      email: "Only letters, numbers, dots (.), underscores (_) and hyphens (-) are allowed before @.",
    }));
    return;
  }

  // 8. Cannot start with dot/underscore/hyphen
  if (/^[._-]/.test(local)) {
    setErrors(prev => ({ ...prev, email: "Email cannot start with a dot, underscore, or hyphen." }));
    return;
  }

  // 9. Cannot end with a dot
  if (local.endsWith(".")) {
    setErrors(prev => ({ ...prev, email: "Local part cannot end with a dot." }));
    return;
  }

  // 10. No consecutive dots
  if (local.includes("..")) {
    setErrors(prev => ({ ...prev, email: "Email cannot contain consecutive dots." }));
    return;
  }

  // 🔥 11. Local part max 64 chars (your main issue)
  if (local.length > 64) {
    setErrors(prev => ({ ...prev, email: "Local part before @ cannot exceed 64 characters." }));
    return;
  }

  // --- If all rules passed → clear errors ---
  setErrors(prev => ({ ...prev, email: "" }));


}}
/>
{errors.email && <p className="uc-error-text">{errors.email}</p>}

<div className="form-field">
              <div className="role-label-row">
                <label>
                  Role <span className="required-asterisk">*</span>
                </label>

                {isCurrentUserAdmin && (
                  <span
                    className="view-role-link"
                    onClick={() => {
                      setSelectedRoleView(newUser.role || "Admin");
                      setShowRoleViewModal(true);
                    }}
                  >
                    View User Role details
                  </span>
                )}

              </div>

<ViewUserRoleModal
  open={showRoleViewModal}
  role={selectedRoleView}
  onChangeRole={(newRole) => setSelectedRoleView(newRole)}
  onClose={() => setShowRoleViewModal(false)}
/>

<CustomSelect
  value={newUser.role}
  bg="gray"
  onChange={(v) => {
    setNewUser({ ...newUser, role: v });
    setErrors({ ...errors, role: "" });
  }}
  width="100%"
  placeholder="Select Role"
  options={[
    "Admin",
    "Product Owner",
    "Product User",
    "Vocovo Viewer",
    // "Vocovo Client",
  ]}
/>
{errors.role && (
  <p className="uc-error-text1">{errors.role}</p>
)}
</div>

              {/* <div className="active-toggle">
                <div
                  className={`toggle-switch ${
                    newUser.active ? "active" : ""
                  }`}
                  onClick={() =>
                    setNewUser({ ...newUser, active: !newUser.active })
                  }
                >
                  <div className="toggle-circle"></div>
                </div>
                <span style={{fontSize:"14px",
                  fontWeight:"500",
                  lineHeight:"20px",
                  color:"#7509B8"
                }}>{newUser.active ? "Active" : "Inactive"}</span>
              </div> */}
            </div>
            )}

            <div className="modal-actions">
              <button
                onClick={() => setShowModal(false)}
                disabled={isUserModalLoading}
                 style={{
    width: "90px",
    height: "44px",
    padding: "10px 16px",
    borderRadius: "8px",
    border: "1px solid #2D0844",
    background: "white",
    color: "#2D0844",
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    letterSpacing: "0",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer"
  }}
              >
                Cancel
              </button>
              <button  disabled={!isUserFormValid() || isUserModalLoading}
  style={{
    width: "130px",
    height: "44px",
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #2D0844",
    background: "#2D0844",
    color: "#FFFFFF",
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    letterSpacing: "0",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    cursor: !isUserFormValid() ? "not-allowed" : "pointer",
    pointerEvents: !isUserFormValid() ? "none" : "auto"
  }} onClick={() =>
  openConfirm({
    title: editingUser ? "Confirm Update" : "Confirm Save",
    message: editingUser
      ? "Do you want to update this user?"
      : "Do you want to create this new user?",
    confirmText: editingUser ? "Update" : "Save",
    confirmType: "primary",
    onConfirm: async () => handleSaveUser(),
  })
}
>
                {isUserModalLoading ? "Loading..." : (editingUser ? "Update User" : "Create User")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      <ConfirmModal
        open={confirmConfig.open}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText="Cancel"
        confirmType={confirmConfig.confirmType}
        onCancel={closeConfirm}
        onConfirm={handleConfirmClick}
      />
      <UserControllerModal
        open={showUserControlModal}
        user={selectedUserForControl}
        onClose={() => setShowUserControlModal(false)}
      />
      {showCustomerDetail && (
  <CustomerDetailDrawer
    data={selectedCustomer}
    loading={isCustomerDetailLoading}
    onClose={() => { setShowCustomerDetail(false); setSelectedCustomer(null); }}
  />
)}
    </div>
  );
};

export default UserManagement;
